<template>
<div class="pl-jc"
     :class="{ 'pl-jc-ghost': flags.preproduction, 'pl-jc-muted': muted, 'pl-jc-typed': job.type && job.type !== 'process', 'pl-jc-click': clickable }"
     :style="{ '--jc': colour || '#6b6a66' }"
     :title="title"
     @click="clickable && $emit('open', job)">
    <div class="pl-jc-r1">
        <b class="pl-jc-nm">{{ name }}</b>
        <span v-if="hours !== null && hours !== undefined" class="pl-jc-h">{{ fmtSecs(hours) }}</span>
    </div>
    <div v-if="showItem || job.deadline || anyFlag || extra" class="pl-jc-r2">
        <span v-if="showItem" class="pl-jc-item">{{ itemText }}</span>
        <span v-if="extra" class="pl-jc-extra">{{ extra }}</span>
        <span v-if="job.deadline" class="pl-jc-ddl" :class="'pl-jc-ddl-' + (job.risk || 'ok')"
              :title="riskTitle">⚑ {{ fmtDay(job.deadline) }}</span>
        <flag-pills :flags="flags" :show-pre="false" />
    </div>
</div>
</template>

<script>
import FlagPills from './FlagPills';
import { fmtDay, fmtSecs, shortenMiddle } from './shared';

/**
 * One magnet: a job as Frances writes it on the wall — client, what it is,
 * the ⚑, the scribbles. Shared by the People board (with the hours the
 * person holds on it that week) and the Dates board (deadline cards).
 * planning/planner-boards-p1-p3-p4-plan.md §3.
 *
 * `job` carries: quote_id, quote_name, company, item_label, items, deadline,
 * risk, pct_complete, flags, and optionally type ('adhoc'/'delivery' for a
 * typed slot standing in for a job). Hatched = still in pre-production.
 */
export default {
    name: 'JobCard',
    components: { FlagPills },

    props: {
        job: { type: Object, required: true },
        hours: { type: Number, default: null },        // seconds — shown top-right when given
        colour: { type: String, default: null },       // left edge — workcentre colour of the stage
        muted: { type: Boolean, default: false },      // past week / done
        clickable: { type: Boolean, default: true },
        extra: { type: String, default: null },        // e.g. "3 of 8 done" — a short free line
    },

    computed: {
        flags() {
            return this.job.flags || {};
        },
        name() {
            return this.job.quote_name || this.job.title || ('Job ' + this.job.quote_id);
        },
        showItem() {
            return !!this.itemText;
        },
        itemText() {
            if (this.job.item_label) return shortenMiddle(this.job.item_label, 40);
            if (this.job.items > 1) return this.job.items + ' items';
            return '';
        },
        anyFlag() {
            const f = this.flags;
            return f.artwork === 'waiting' || !!f.priority || !!f.redo || !!f.tbc;
        },
        title() {
            const parts = [];
            if (this.job.quote_id) parts.push('Q' + this.job.quote_id);
            parts.push(this.name);
            if (this.job.company) parts.push(this.job.company);
            if (this.job.item_label) parts.push(this.job.item_label);
            if (this.job.pct_complete !== undefined && this.job.pct_complete !== null) parts.push(this.job.pct_complete + '% done');
            return parts.join(' · ');
        },
        riskTitle() {
            if (this.job.risk === 'late') return 'Deadline missed or work runs past it';
            if (this.job.risk === 'tight') return 'Work finishes within 2 days of the deadline';
            return 'On track';
        },
    },

    methods: { fmtDay, fmtSecs },
};
</script>

<style scoped>
.pl-jc{border:1px solid #d9d7ce; border-left:3px solid var(--jc); border-radius:6px; padding:5px 7px 5px 8px;
    background:#fcfcfb; font-size:12px; line-height:1.25; display:grid; gap:3px; min-width:0; user-select:none}
.pl-jc-click{cursor:pointer}
.pl-jc-click:hover{border-color:#0b0b0b}
.pl-jc-ghost{background:repeating-linear-gradient(135deg, #fcfcfb 0 6px, #f1f0eb 6px 9px)}
.pl-jc-typed{border-left-style:dashed}
.pl-jc-muted{opacity:.55}
.pl-jc-r1{display:flex; justify-content:space-between; gap:8px; align-items:baseline; min-width:0}
.pl-jc-nm{font-weight:700; color:#0b0b0b; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0}
.pl-jc-h{font-weight:700; color:#52514e; font-size:11.5px; white-space:nowrap; font-variant-numeric:tabular-nums}
.pl-jc-r2{display:flex; gap:5px; flex-wrap:wrap; align-items:center; color:#898781; font-size:11px; min-width:0}
.pl-jc-item{white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:100%}
.pl-jc-extra{color:#52514e; font-weight:600}
.pl-jc-ddl{font-weight:800; font-size:10.5px; border-radius:4px; padding:0 5px; line-height:17px; white-space:nowrap}
.pl-jc-ddl-ok{color:#0a7a0a; background:#e3f4e3}
.pl-jc-ddl-tight{color:#8a5a00; background:#fdf3d9}
.pl-jc-ddl-late{color:#b32e2e; background:#f9e4e4}
</style>
